import React, { useMemo } from 'react';
import { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import TableWrapper from '@/components/atoms/TableWrapper';
import CustomTable from '@/components/atoms/CustomTable';
import {
  useGetConfigLogsQuery,
  ConfigLogItem,
  ActionCode,
} from '@/store/services/configLogsService';
import AdminLogDetailsPopup from './AdminLogDetailsPopup';
import Action from './Action';
import LogsFilterModal, { LogsFilters } from './LogsFilterModal';
import LogsTableFilters from './LogsTableFilters';
import {
  setModalId,
  setPage,
  usePublicState,
} from '@/store/slices/publicSlice';

export const LogsTable = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const { search, modalExtraData, page } = usePublicState();

  const pageSize = 10;
  const searchValue = search?.configLogs || '';

  const filters = useMemo(() => {
    return (
      (modalExtraData as { configLogsFilters?: LogsFilters })
        ?.configLogsFilters || {}
    );
  }, [modalExtraData]);

  const {
    data: logsData,
    isLoading,
    isFetching,
  } = useGetConfigLogsQuery(
    {
      page,
      limit: pageSize,
      search: searchValue || undefined,
      roleCodes: filters?.roles?.length ? filters.roles : undefined,
      functionNames: filters?.functionNames?.length
        ? filters.functionNames
        : undefined,
      actionCodes: filters?.actions?.length ? filters.actions : undefined,
      dateFrom: filters?.dateFrom || undefined,
      dateTo: filters?.dateTo || undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  dayjs.locale(language);


  const columns: TableProps<ConfigLogItem>['columns'] = [
    {
      title: t('configLogs.userName', 'User'),
      dataIndex: 'userName',
      key: 'userName',
      render: (val) => val || '-',
    },
    {
      title: t('configLogs.role', 'Role'),
      dataIndex: 'role',
      key: 'role',
      render: (val) => val?.name || '-',
    },
    {
      title: t('configLogs.dateTime', 'Date & time'),
      dataIndex: 'date',
      key: 'date',
      render: (val) => {
        if (!val) return '-';
        return dayjs(val).isValid()
          ? dayjs(val).format('DD MMM YYYY - hh:mm a')
          : val;
      },
    },
    {
      title: t('configLogs.functionName', 'Function name'),
      dataIndex: 'functionName',
      key: 'functionName',
      render: (val) => val || '-',
    },
    {
      title: t('configLogs.placement', 'Placement'),
      dataIndex: 'placement',
      key: 'placement',
      render: (val) => val || '-',
      width:300
    },
    {
      title: t('configLogs.action', 'Action'),
      dataIndex: 'action',
      key: 'action',
      render: (action: { name: string; code: ActionCode }) => (
        <Action action={action} />
      ),
    },
  ];

  return (
    <>
      <TableWrapper
        title={t('configLogs.tableTitle', 'Logs')}
        subTitle={t(
          'configLogs.tableSubTitle',
          'Display all Configurations Audit logs'
        )}
        filters={<LogsTableFilters />}
      >
        <CustomTable<ConfigLogItem>
          rowKey="id"
          loading={isLoading || isFetching}
          currentPage={page}
          pageSize={pageSize}
          totalElements={logsData?.payload?.totalElements || 0}
          onPageChange={(pageNumber) => {
            dispatch(setPage(pageNumber - 1));
          }}
          columns={columns}
          dataSource={logsData?.payload?.content || []}
          onRowClick={(_, record) => {
            const id = record.id;
            dispatch(setModalId({ id, extraData: modalExtraData }));
          }}
        />
      </TableWrapper>

      <AdminLogDetailsPopup
        listOfIds={logsData?.payload?.content?.map((i) => i.id) as string[]}
      />

      <LogsFilterModal />
    </>
  );
};

export default LogsTable;

